/**
 * STRYDE cart drawer controls (snippets/stryde-cart.liquid).
 * Quantity and remove change the line by its key (safe when the cart changed in another tab) and hand the
 * re-rendered drawer to Horizon through a CartLinesUpdateEvent, so the drawer and cart icon update as usual.
 * Adds (upsell brace, shipping protection) go through Shopify.actions.updateCart.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

if (!window.strydeCartBound) {
  window.strydeCartBound = true;

  const SECTION = 'cart-drawer-section';

  /** @param {Element} el @param {boolean} busy */
  const setBusy = (el, busy) => el.closest('[data-sc]')?.classList.toggle('sc-busy', busy);

  /** @param {Element} el @param {string} key @param {number} quantity */
  const change = async (el, key, quantity) => {
    setBusy(el, true);
    const deferred = CartLinesUpdateEvent.createPromise();
    // Dispatched on document so Horizon's cart-items-component treats it as an external update and morphs the drawer.
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferred.promise,
      })
    );
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: SECTION, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.errors) throw new Error(data.description || data.errors || 'Cart update failed');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { sections: data.sections, items: data.items, itemCount: data.item_count, source: 'stryde-cart', didError: false },
      });
    } catch (error) {
      console.error('[stryde] cart update failed', error);
      // Resolve without sections so Horizon refetches the drawer and shows the real cart.
      deferred.resolve({ detail: { source: 'stryde-cart', didError: true } });
    } finally {
      setBusy(el, false);
    }
  };

  /** After the 3 → 4 upsell: say what it saved, once, where the best-deal line appears. @param {string} text */
  const sayDone = (text) => {
    let tries = 0;
    const timer = setInterval(() => {
      const best = document.querySelector('[data-sc] [data-sc-best]');
      if (best) best.textContent = text;
      if (best || ++tries > 20) clearInterval(timer);
    }, 100);
  };

  /** @param {Element} el @param {string} variantId @param {{key: string, value: string}[]} attributes */
  const add = async (el, variantId, attributes) => {
    setBusy(el, true);
    const done = el instanceof HTMLElement ? el.dataset.scDone : undefined;
    try {
      await /** @type {any} */ (window).Shopify?.actions?.updateCart(
        { lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1, attributes }] },
        { event: { context: 'cart' } }
      );
      if (done) sayDone(done);
    } catch (error) {
      console.error('[stryde] cart update failed', error);
    } finally {
      setBusy(el, false);
    }
  };

  document.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);

    const qty = target.closest?.('[data-sc-qty]');
    if (qty instanceof HTMLElement) {
      change(qty, qty.dataset.key ?? '', Number(qty.dataset.scQty));
      return;
    }

    const up = target.closest?.('[data-sc-add]');
    // No line properties: the same variant merges into one line with a higher quantity.
    if (up instanceof HTMLElement) add(up, up.dataset.scAdd ?? '', []);
  });

  document.addEventListener('change', (event) => {
    const sw = event.target;
    if (!(sw instanceof HTMLInputElement) || !sw.matches('[data-sc-prot]')) return;
    const key = sw.dataset.key ?? '';
    if (sw.checked && !key) add(sw, sw.dataset.scProt ?? '', []);
    else if (!sw.checked && key) change(sw, key, 0);
  });
}
